import { motion } from 'framer-motion';

/**
 * QuizDiagnosticBar — Sticky pill that hooks the user with a "diagnostic en cours"
 * message that gets enriched as they progress. Creates a carrot effect — they want
 * to keep going to see the full diagnostic.
 *
 * Props:
 *   - currentScreen: number (0-based)
 *   - totalScreens: number
 *   - estMinutes: estimated minutes remaining
 *   - blocsCompletes: array of strings (e.g. ["Profil", "Finance"])
 */
const QuizDiagnosticBar = ({ currentScreen, totalScreens, estMinutes, blocsCompletes = [] }) => {
  const pct = Math.round(((currentScreen + 1) / totalScreens) * 100);

  // Status message that evolves with progress
  let status = "🔍 Audit en cours...";
  if (pct >= 90) status = "✨ Finalisation de ton audit...";
  else if (pct >= 70) status = "📊 Calcul de tes pertes en cours...";
  else if (pct >= 40) status = "🎯 Analyse de tes angles morts...";
  else if (pct >= 15) status = "📥 Collecte des données pour ton audit...";

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex items-center justify-between gap-3 mt-3 mb-1"
      data-testid="quiz-diagnostic-bar"
    >
      {/* Live status pill (left) */}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <motion.span
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{
            background: '#F05A1A',
            boxShadow: '0 0 8px rgba(240,90,26,0.7)',
          }}
          animate={{ opacity: [1, 0.35, 1] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
        />
        <p className="text-[11px] font-bold truncate" style={{ color: '#1a1a1a' }}>
          {status}
        </p>
        {blocsCompletes.length > 0 && (
          <span
            className="text-[9px] font-black px-1.5 py-0.5 rounded-full whitespace-nowrap"
            style={{
              background: 'rgba(34,197,94,0.12)',
              color: '#15803D',
              border: '1px solid rgba(34,197,94,0.3)',
            }}
          >
            ✓ {blocsCompletes.length} bloc{blocsCompletes.length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Time remaining (right) */}
      {estMinutes !== null && estMinutes !== undefined && (
        <motion.div
          key={estMinutes}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center gap-1 text-[10px] font-bold whitespace-nowrap flex-shrink-0"
          style={{ color: '#6B7280' }}
        >
          <span>⏱️</span>
          <span>{estMinutes < 1 ? '< 1' : `~${estMinutes}`} min</span>
        </motion.div>
      )}
    </motion.div>
  );
};

export default QuizDiagnosticBar;
